<template>
    <div v-if="displayUpdateRuleSelection">
        <UpdateRuleSelector :onSelect="handleUpdateRuleSelection" :onCancel="handleUpdateRuleCancel" title="Atualizar agendamento" />
    </div>
    <div v-else>
        <form class="scheduling-form" v-on:submit.prevent="handleSubmit" ref="form">
            <div class="container-fluid">
                <div class="row">
                    <div :class="beginDateClass">
                        <label for="begin_date">Data de início</label>
                        <DatepickerInput name="begin_date" :default-date="currentBeginDate" :on-selection="handleBeginDateSelection" />
                        <span class="form-error">{{ validationMessage('begin_date') }}</span>
                    </div>
                    <div v-if="selectedAnyRecurrency" class="col-md-4 form-group">
                        <label for="end_date">Data de término</label>
                        <DatepickerInput name="end_date" :default-date="currentEndDate" :on-selection="handleEndDateSelection" />
                        <span class="form-error">{{ validationMessage('end_date') }}</span>
                    </div>
                    <div class="col-md-4 form-group">
                        <label for="duration_minutes">Duração</label>
                        <select class="form-control" v-model="values.duration_minutes" required name="duration_minutes" id="duration_minutes">
                            <option v-for="option in product.scheduling_options.duration_minutes" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('duration_minutes') }}</span>
                    </div>
                </div>
                <div v-if="withDateAndTime" class="row">
                    <div class="col-md-3 form-group">
                        <label for="consumable_from_minutes">Horário de início</label>
                        <select class="form-control" v-model="values.consumable_from_minutes" name="consumable_from_minutes" id="consumable_from_minutes">
                            <option v-for="option in consumableFromMinutesOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('consumable_from_minutes') }}</span>
                    </div>
                    <div class="col-md-3 form-group">
                        <label for="consumable_to_minutes">Horário de término</label>
                        <select class="form-control" v-model="values.consumable_to_minutes" name="consumable_to_minutes" id="consumable_to_minutes">
                            <option v-for="option in consumableToMinutesOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('consumable_to_minutes') }}</span>
                    </div>
                    <div class="col-md-3 form-group">
                        <label for="calendar_interval_minutes">Intervalo no calendário</label>
                        <select class="form-control" v-model="values.calendar_interval_minutes" name="calendar_interval_minutes" id="calendar_interval_minutes">
                            <option v-for="option in calendarIntervalMinutesOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('calendar_interval_minutes') }}</span>
                    </div>
                    <div class="col-md-3 form-group">
                        <label for="antecedence_hours">Antecedência mínima (horas)</label>
                        <input type="number" class="form-control" v-model="values.antecedence_hours" required min="0" name="antecedence_hours" id="antecedence_hours" />
                        <span class="form-error">{{ validationMessage('antecedence_hours') }}</span>
                    </div>
                </div>
                <div v-else-if="withDateOnly" class="row">
                    <div class="col-md-6 form-group">
                        <label for="antecedence_hours">Antecedência mínima (horas)</label>
                        <input type="number" class="form-control" v-model="values.antecedence_hours" required min="0" name="antecedence_hours" id="antecedence_hours" />
                        <span class="form-error">{{ validationMessage('antecedence_hours') }}</span>
                    </div>
                    <div class="col-md-6 form-group">
                        <label for="antecedence_hours_reference_minutes">Horário de referência para antecedência</label>
                        <select class="form-control" v-model="values.antecedence_hours_reference_minutes" name="antecedence_hours_reference_minutes" id="antecedence_hours_reference_minutes">
                            <option v-for="option in antecedenceHoursReferenceMinutesOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('antecedence_hours_reference_minutes') }}</span>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group">
                        <label for="recurrency_amount">
                            <span>Repetir</span>
                        </label>
                        <input type="number" class="form-control" v-model="values.recurrency_amount" required min="1" name="recurrency_amount" id="recurrency_amount" :disabled="!selectedAnyRecurrency" />
                        <span class="form-error">{{ validationMessage('recurrency_amount') }}</span>
                    </div>
                    <div class="col-md-3 form-group">
                        <label for="recurrency_type">A cada</label>
                        <select class="form-control" v-model="values.recurrency_type" name="recurrency_type" id="recurrency_type" v-on:change="handleRecurrencyTypeChange">
                            <option v-for="option in recurrencyTypeOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('recurrency_type') }}</span>
                    </div>
                    <div v-if="shouldDisplayRecurrencyWeekdays" class="col-md-6 form-group">
                        <div>
                            <label>Dias da semana</label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input type="checkbox" name="all-weekdays" id="all-weekdays" class="form-check-input" v-model="allWeekdaysAreSelected" v-on:change="handleAllWeekdaysSelection" />
                            <label class="form-check-label" for="all-weekdays">Marcar todos</label>
                        </div>
                        <div v-for="option in recurrencyWeekdaysOptions" class="form-check form-check-inline">
                            <input type="checkbox" :name="`recurrency_weekdays[${option.value}]`" v-model="currentWeekdays[option.value]" :id="`recurrency_weekdays[${option.value}]`" class="form-check-input tariff-weekday-check" :value="option.value" v-on:change="handleWeekdaySelection" />
                            <label class="form-check-label" :for="`recurrency_weekdays[${option.value}]`">{{ option.label }}</label>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6 form-group">
                        <label for="id_tariff_group">Grupo de tarifas</label>
                        <select class="form-control" v-model="values.id_tariff_group" name="id_tariff_group" id="id_tariff_group">
                            <option v-for="option in idTariffGroupOptions" :value="option.value">{{ option.label }}</option>
                        </select>
                        <span class="form-error">{{ validationMessage('id_tariff_group') }}</span>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <span class="form-error">{{ validationMessage('general') }}</span>
                        <div v-if="dateErrorMessages" v-for="str in dateErrorMessages">{{ str }}</div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-6 col-md-2 offset-md-8" v-if="onCancel">
                        <button v-on:click.prevent="onCancel" class="btn btn-outline-primary btn-block">Cancelar</button>
                    </div>
                    <div class="col-6 col-md-2">
                        <button class="btn btn-primary btn-block">Salvar</button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import DatepickerInput from './DatepickerInput';
import UpdateRuleSelector from './UpdateRuleSelector';
import { dateFromDateString, formatDate, weekdayName } from '../../common/calendar';

const schedulingErrors = {
    conflict_error: 'As seguintes datas conflitam',
    coupling_error: 'As seguintes datas já possuem agendamentos'
};

export default {
    props: {
        product: Object,
        scheduling: Object,
        onSubmit: Function,
        onCancel: Function,
        validation: Object
    },
    data(){
        let values = {
            duration_minutes: this.product.scheduling_options.duration_minutes[0].value,
            antecedence_hours: 0,
            recurrency_type: '',
            id_tariff_group: ''
        };

        if(this.product.selling_mode === 'with_date_and_time'){
            values = {
                ...values,
                consumable_from_minutes: this.product.scheduling_options.consumable_from_minutes[0].value,
                consumable_to_minutes: this.product.scheduling_options.consumable_to_minutes[0].value,
                calendar_interval_minutes: this.product.scheduling_options.calendar_interval_minutes[0].value,
            };
        }
        else{
            values = { ...values, antecedence_hours_reference_minutes: this.product.scheduling_options.antecedence_hours_reference_minutes[this.product.scheduling_options.antecedence_hours_reference_minutes.length - 1].value };
        }

        values = { ...values, ...this.scheduling };

        const currentWeekdays = this.scheduling.recurrency_weekdays ? this.scheduling.recurrency_weekdays.reduce((obj, weekday) => ({ ...obj, [weekday]: true }), {}) : null;

        return {
            values,
            currentBeginDate: this.convertToDateObj(this.scheduling.begin_date),
            currentEndDate: this.convertToDateObj(this.scheduling.end_date),
            currentWeekdays,
            allWeekdaysAreSelected: false,
            displayUpdateRuleSelection: false
        }
    },
    computed: {
        alreadyExists(){
            return this.scheduling.id != null;
        },
        withDateAndTime(){
            return this.product.selling_mode === 'with_date_and_time';
        },
        withDateOnly(){
            return this.product.selling_mode === 'with_date_only';
        },
        beginDateClass(){
            return `col-md-${this.selectedAnyRecurrency ? 4 : 8} form-group`;
        },
        durationMinutesOptions(){
            return this.product.scheduling_options.duration_minutes;
        },
        consumableFromMinutesOptions(){
            return this.product.scheduling_options.consumable_from_minutes;
        },
        consumableToMinutesOptions(){
            return this.product.scheduling_options.consumable_to_minutes;
        },
        calendarIntervalMinutesOptions(){
            return this.product.scheduling_options.calendar_interval_minutes;
        },
        antecedenceHoursReferenceMinutesOptions(){
            return this.product.scheduling_options.antecedence_hours_reference_minutes;
        },
        recurrencyTypeOptions(){
            return [
                { value: '', label: 'Nenhuma'},
                { value: 'days', label: 'Dia'},
                { value: 'weeks', label: 'Semana'}
            ];
        },
        recurrencyWeekdaysOptions(){
            const opts = [];
            for(let value = 1; value <= 7; value++){
                opts.push({ value, label: weekdayName(value) });
            }

            return opts;
        },
        selectedAnyRecurrency(){
            return !!this.values.recurrency_type;
        },
        shouldDisplayRecurrencyWeekdays(){
            return this.values.recurrency_type === 'weeks';
        },
        idTariffGroupOptions(){
            return [
                { value: '', label: 'Nenhum' },
                ...this.product.tariff_groups.map(group => ({ value: group.id, label: group.name }))
            ];
        },

        dateErrorMessages(){
            if(this.validation == null || !this.validation.error_dates){
                return null;
            }

            const arr = [];
            for(let key in this.validation.error_dates){
                const formattedError = schedulingErrors[key] || '';
                const dateStr = this.validation.error_dates[key].map(str => formatDate(dateFromDateString(str))).join(', ');

                arr.push(`${formattedError}: ${dateStr}`);
            }

            return arr;
        }
    },
    methods: {
        convertToDateObj(str){
            return str ? dateFromDateString(str) : null;
        },
        handleBeginDateSelection(date){
            this.currentBeginDate = date;
            this.values.begin_date = date.dateString;
        },
        handleEndDateSelection(date){
            this.currentEndDate = date;
            this.values.end_date = date.dateString;
        },
        handleRecurrencyTypeChange(ev){
            const { value } = ev.target;
            switch(value){
                case 'weeks':
                    this.currentWeekdays = {};
                    this.values.recurrency_weekdays = [];
                    break;
                case 'days':
                    this.currentWeekdays = null;
                    this.values.recurrency_weekdays = null;
                    break;
                default:
                    this.currentWeekdays = null;
                    this.values.recurrency_amount = null;
                    this.values.recurrency_weekdays = null;
            }
        },

        handleWeekdaySelection(ev){
            this.updateRecurrencyWeekdays();
            this.refreshAllWeekdaysAreSelected();
        },
        updateRecurrencyWeekdays(){
            const arr = [];
            for(let key in this.currentWeekdays){
                if(this.currentWeekdays[key]){
                    arr.push(parseInt(key));
                }
            }

            this.values.recurrency_weekdays = arr;
        },
        refreshAllWeekdaysAreSelected(){
            this.allWeekdaysAreSelected = this.recurrencyWeekdaysOptions.every(({ value }) => this.currentWeekdays && this.currentWeekdays[value]);
        },
        handleAllWeekdaysSelection(){
            this.currentWeekdays = this.recurrencyWeekdaysOptions.reduce((obj, { value }) => ({ ...obj, [value]: this.allWeekdaysAreSelected }), {});
            this.updateRecurrencyWeekdays();
        },

        validationMessage(key){
            const { validation: val } = this;
            return val && val[key] ? val[key].join(', ') : '';
        },
        handleSubmit(){
            this.values.antecedence_minutes = this.values.antecedence_hours * 60;

            if(this.alreadyExists){
                this.displayUpdateRuleSelection = true;
                return;
            }

            this.onSubmit(this.values);
        },
        handleUpdateRuleCancel(){
            this.displayUpdateRuleSelection = false;
        },
        handleUpdateRuleSelection(rule){
            this.values.update_rule = rule;
            this.displayUpdateRuleSelection = false;

            this.onSubmit(this.values);
        }
    },
    mounted(){
        this.refreshAllWeekdaysAreSelected();
    },
    components: {
        DatepickerInput,
        UpdateRuleSelector
    }
}
</script>

<style>
.form-error {
    font-weight: bold;
    color: #ea4335;
}
</style>
